// Styles
import './styles.scss';

// Core
import React, { Component } from 'react';
import { connect } from 'react-redux';
// Redux
import { batchActions } from 'redux-batched-actions';
import {
    updateAccount,
    updateEvent,
    updateAttendance,
    updateContact,
    replaceHosts,
    replaceSessions,
} from '../../redux/actions';

// Constants
import {
    ATTENDEE_EJECTED_EVENT,
    STREAM_CONNECTED_EVENT,
    STREAM_ACTIVE_EVENT,
    STREAM_DISCONNECTED_EVENT,
    STREAM_IDLE_EVENT
} from './constants';

// Services
import GeckoStore from '../../services/GeckoStore';
import { Listener, UnbindListener } from './services/_';
import AdminService from './services/AdminService';
import AttendanceService from './services/AttendanceService';
import ContactService from './services/ContactService';
import DataService from './services/DataService';
import EventService from './services/EventService';
import StreamService from './services/StreamService';
import SocketService from './services/SocketService';
import PresenceSocketService from './services/PresenceSocketService';
import RegionService from './services/RegionService';
import DateUtils from '../../services/DateUtils';
import { $stateParams } from '../../router';

// Components
import {
    ErrorLayout,
    JoinedLayout
} from './components/Layouts';
import ChatUserNameService from './services/ChatUserNameService';

let listeners;

export class StreamPage extends Component {

    state = {
        error: false
    };

    errorFn(err) {
        this.setState({error: err.toString()});
    }

    checkinFn(data) {
        return AttendanceService.checkinFn(this.props.event.id).then(() => {
            // Update attendance status (in store)
            AttendanceService.attend();
        }).catch(this.errorFn.bind(this));
    }

    dataCheckFn(skipDataCheck = false) {
        if (!skipDataCheck && !EventService.checkinHasStarted(this.props.event)) {
            return DataService.get().then(data => updateReduxStore(data));
        }
        // Dont refresh data when we know event checkin has already started
        return Promise.resolve();
    }

    initFn(viewUrl, skipDataCheck = false) {
        let promise;
        // Skip self checkin when admin
        if (AdminService.isAdmin()) {
            promise = Promise.resolve().then(() => StreamService.load(viewUrl));
        } else {
            promise = this.dataCheckFn(skipDataCheck).then(() => {
                const event             = this.props.event;
                const attendance        = this.props.attendance;
                const checkinHasStarted = EventService.checkinHasStarted(event);
                const checkinHasEnded   = EventService.checkinHasEnded(event);
                const attendeeCanJoin   = AttendanceService.canJoin(attendance.status);
                const attendeeHasJoined = AttendanceService.hasJoined(attendance.status);

            // Event has self checkin ENABLED && event checkin has STARTED
                if (checkinHasStarted && !checkinHasEnded && attendeeCanJoin) {
                    // Attendee still needs to check in BEFORE stream is accessed
                    return this.checkinFn().then(() => StreamService.load(viewUrl)).catch(this.errorFn.bind(this));
            // Attendee has already checked in
                } else if (attendeeHasJoined) {
                    return StreamService.load(viewUrl);
                }
            });
        }

        return promise.then(this.presenceSocketInit.bind(this)).catch(this.errorFn.bind(this));
    }

    socketInit() {
        SocketService.init();

        const connectedHandler = (data) => {
            console.log('--- STREAM CONNECTED ---', data);
            this.initFn(data.view_url);
        };

        const activeHandler = (data) => {
            console.log('--- STREAM ACTIVE ---', data);
            this.initFn(data.view_url);
        };

        const disconnectedHandler = (data) => {
            console.log('--- STREAM DISCONNECTED ---');
            StreamService.disconnect();
        };

        const idleHandler = (data) => {
            console.log('--- STREAM ENDED ---');
            StreamService.idle();
        };

        const attendeeEjectedHandler = ({contact_secret}) => {
            if (ContactService.encryptUuid($stateParams.contact_uuid) === contact_secret) {
                return PresenceSocketService.destroy().then(() => {
                    window.location.reload();
                }).catch(console.log);
            }
        };

        listeners = [
            Listener(STREAM_CONNECTED_EVENT, connectedHandler),
            Listener(STREAM_ACTIVE_EVENT, activeHandler),
            Listener(STREAM_DISCONNECTED_EVENT, disconnectedHandler),
            Listener(STREAM_IDLE_EVENT, idleHandler),
            Listener(ATTENDEE_EJECTED_EVENT, attendeeEjectedHandler)
        ];
    }

    presenceSocketInit() {
        const chatIsEnabled     = EventService.chatIsEnabled(this.props.event);
        const isAdmin           = AdminService.isAdmin();
        const attendeeHasJoined = AttendanceService.hasJoined(this.props.attendance.status);

        // Dont init presence chanel when chat is DISABLED for the event
        if (!chatIsEnabled) return;

        // DONT init presence channel when attendee is NOT ATTENDED or ENGAGED
        if (!isAdmin && !attendeeHasJoined) return;

        return PresenceSocketService.destroy().then(() => {
            const storedUsername = ChatUserNameService.storedName();
            return storedUsername ? PresenceSocketService.init(storedUsername) : PresenceSocketService.init('Anonymous');
        });
    }

    UNSAFE_componentWillMount() {
    // Parse and store page data
        Promise.resolve().then(() => {
            let data = this.props.data;

            // Set page title
            document.title = `${data.event ? data.event.title : ''} | GeckoEngage`;

            // Is admin
            AdminService.setIsAdmin(data.is_admin || data.is_host);

            // Store top level EVENT id
            EventService.setEventId(data.event.parent_id || data.event.id);

            // Store contact uuid map
            ContactService.setSecretToUuidMap(data.secret_to_uuid_map);

            // Set time format (from account settings)
            DateUtils.setTimeFormatFromAccount(data.account.time_format);

            // Set locale for date formating
            DateUtils.setLocale();

            // Set timezone for event & sessions
            DateUtils.setTimezone(data.event.timezone);

            // Set API region
            RegionService.setRegion(data.account.region);

            // Update redux store
            updateReduxStore(data);
    // Setup socket events
        }).then(() => {
            this.socketInit();
    // Attempt to start the stream (and join the event if required)
        }).then(() => {
            const viewUrl = ((this.props.event || {}).video || {}).view_url || null;
            return this.initFn(viewUrl, true);
        }).catch(this.errorFn.bind(this));
    }

    // On Destroy
    componentWillUnmount() {
        // Unbind all listeners
        UnbindListener(listeners);
        // Close (Pusher) connections
        SocketService.destroy();
        PresenceSocketService.destroy();
    }

    render() {
        const attrs = {
            account: this.props.account,
            attendance: this.props.attendance,
            contact: this.props.contact,
            event: this.props.event,
            hosts: this.props.hosts,
            sessions: this.props.sessions,
            stream: this.props.stream,
            ignore_account_branding: this.props.ignore_account_branding,
        };

        const isAdmin                = AdminService.isAdmin();
        const attendeeHasJoined      = AttendanceService.hasJoined(attrs.attendance.status);
        const attendeeCantJoin       = AttendanceService.cantJoin(attrs.attendance.status);
        const eventCheckinHasStarted = EventService.checkinHasStarted(attrs.event);
        const eventCheckinHasEnded   = EventService.checkinHasEnded(attrs.event);

        let layout;
    // There is an error, show it
        if (this.state.error) {
            layout = (<ErrorLayout {...attrs} error={this.state.error} />);
    // Attendee (status) CANT join the event e.g. removed, cancelled
        } else if (!isAdmin && attendeeCantJoin) {
            layout = (<ErrorLayout {...attrs} error={'You are unable to join this event/session.'} />);
    // Attendee has NOT joined and checkin has NOT started, dont let the attendee in
        } else if (!isAdmin && !attendeeHasJoined && !eventCheckinHasStarted) {
            layout = (<ErrorLayout {...attrs} error={'This event/session has not started.'} />);
    // Attendee has NOT joined and event has now ENDEDED, dont let the attendee in
        } else if (!isAdmin && !attendeeHasJoined && eventCheckinHasEnded) {
            layout = (<ErrorLayout {...attrs} error={'This event/session has now finished.'} />);
    // Show page UI including pre-event overlay (if needed)
        } else {
            layout = (
                <>
                    <JoinedLayout attrs={attrs} />
                </>
            );
        }

        return (
            <div>
                {layout}
                <BrandingStyles
                    ignoreAccountBranding={attrs.ignore_account_branding}
                    eventColor={this.props.event.color}
                    accountColor={this.props.account.color}
                />
            </div>
        );
    }
}

const checkBrandColor = (eventColor, accountColor) => {
    const color = eventColor || accountColor;
    return !['#FFFFFF', '#ffffff'].includes(color) ? color : null;
}

const BrandingStyles = ({ignoreAccountBranding, eventColor, accountColor}) => {
    let brandColor = checkBrandColor(eventColor, accountColor);
    if (!brandColor) return null;

    if (ignoreAccountBranding) {
        brandColor = "#000";
    }

    const styles = `
        .stream-page .branded-text { color: ${brandColor}!important; }
        .stream-page .branded-border { border-color: ${brandColor} !important; }
        .stream-page .branded-border-hover:hover { border-color: ${brandColor} !important; }
    `;
    return (<style type="text/css" dangerouslySetInnerHTML={{ __html: styles }}></style>);
};

const updateReduxStore = (data) => {
    let actions = [];
    if (data.account) actions.push(updateAccount(data.account));
    if (data.event) actions.push(updateEvent(data.event));
    if (data.attendance) actions.push(updateAttendance(data.attendance));
    if (data.hosts) actions.push(replaceHosts(data.hosts));
    if (data.sessions) actions.push(replaceSessions(data.sessions));
    if (data.contact) actions.push(updateContact(data.contact));
    GeckoStore.redux.dispatch(batchActions(actions));
};

const mapStateToProps = store => {
    return {
        account: store.account,
        event: store.event,
        attendance: store.attendance,
        hosts: store.hosts,
        sessions: store.sessions,
        stream: store.stream,
        contact: store.contact,
        ignore_account_branding: Boolean(eval($stateParams.ignore_account_branding)),
    }
};

export default connect(mapStateToProps)(StreamPage);